import { fetchRoleOptions } from "@app/api/role";
import { http } from "@shared/utils/request";
import type { FetchListOptions, ListResult } from "@shared/types/list";
import type { CurrentUserInfoData, UserMenuItem, UserMenusData } from "@shared/types/user";
import type { SystemProfileDetail, UpdateProfilePayload } from "@app/views/system/personalCenter/types";
import { mapUserDetailToProfile } from "@app/views/system/personalCenter/mapUserDetail";
import { toUserListRequestParams } from "@app/views/system/user/config/filter";
import { mapUserListItemToRow } from "@app/views/system/user/mapUserListItem";
import type {
    SystemUserQueryParam,
    SystemUserRow,
    UserAddPayload,
    UserEditPayload,
    UserListApiItem,
    UserLoginLogListItem,
    UserLoginLogsQuery,
    UserManagementApi,
    UserResetPasswordPayload,
} from "@app/views/system/user/types";
import { mapUserLoginLogListItem } from "@app/views/system/user/types";

/** 当前登录用户详情 */
export function fetchCurrentUserInfo(): Promise<CurrentUserInfoData> {
    return http.get<CurrentUserInfoData>("/customer/user/info");
}

/** 当前登录用户菜单 */
export function fetchCurrentUserMenus(): Promise<UserMenuItem[]> {
    return http.get<UserMenusData>("/customer/menus").then(res => res.items ?? []);
}

interface UserListApiData {
    items: UserListApiItem[];
    total: number;
}

/** 企业内用户列表 */
export function fetchUserList(params: SystemUserQueryParam, options?: FetchListOptions): Promise<ListResult<SystemUserRow>> {
    return http.get<UserListApiData>("/customer/user/list", toUserListRequestParams(params), { signal: options?.signal }).then(res => ({
        list: (res.items || []).map(mapUserListItemToRow),
        total: res.total ?? 0,
    }));
}

/** 添加企业用户 */
export function addUser(payload: UserAddPayload): Promise<{ id: string }> {
    return http.post<{ id: string }>("/customer/user/add", payload);
}

/** 编辑企业用户（姓名 / 手机 / 邮箱） */
export function editUser(payload: UserEditPayload): Promise<void> {
    return http.post<void>("/customer/user/edit", payload);
}

/** 重置密码 */
export function resetUserPassword(payload: UserResetPasswordPayload): Promise<void> {
    return http.post<void>("/customer/user/resetPassword", payload);
}

/** 批量启用 / 禁用 */
export function setUserStatus(ids: string[], status: number): Promise<void> {
    return http.post<void>("/customer/user/setStatus", { ids, status });
}

/** 软删企业用户 */
export function deleteUser(id: string): Promise<void> {
    return http.post<void>("/customer/user/delete", { id });
}

interface UserLoginLogListApiData {
    items: UserLoginLogListItem[];
    total: number;
}

/** 分页查询指定用户登录日志 */
export function fetchUserLoginLogs(params: UserLoginLogsQuery, options?: FetchListOptions): Promise<ListResult<UserLoginLogListItem>> {
    return http.post<UserLoginLogListApiData>("/customer/user/loginLogs", { userId: params.userId, page: params.page, perPage: params.limit }, { signal: options?.signal }).then(res => ({
        list: (res.items || []).map(mapUserLoginLogListItem),
        total: res.total ?? 0,
    }));
}

/** 用户管理页注入包 */
export const userManagementApi: UserManagementApi = {
    fetchList: fetchUserList,
    add: addUser,
    edit: editUser,
    resetPassword: resetUserPassword,
    setStatus: setUserStatus,
    delete: deleteUser,
    fetchLoginLogs: fetchUserLoginLogs,
    fetchRoleOptions,
};

/** 修改当前登录用户密码 */
export function changeCurrentPassword(oldPassword: string, password: string): Promise<void> {
    return http.post<void>("/customer/user/changePassword", { oldPassword, password });
}

/** 更新当前登录用户资料（用户名 / 昵称 / 邮箱 / 头像 URL） */
export function updateCurrentProfile(payload: UpdateProfilePayload): Promise<void> {
    return http.post<void>("/customer/user/updateProfile", payload);
}

/** 个人中心：加载资料 */
export async function loadCurrentProfile(): Promise<SystemProfileDetail> {
    const data = await fetchCurrentUserInfo();
    return mapUserDetailToProfile(data.userDetail);
}
